import * as React from 'react';
import { useTheme } from '../theme-provider';
export const useAnimatedIndicator = (tabBarRef, tabsRef, activeIndex) => {
    const { colors, direction } = useTheme();
    const updateIndicatorPosition = React.useCallback((activeIndex) => {
        var _a, _b;
        const activeTab = tabsRef.current[activeIndex];
        if (!activeTab || !tabBarRef.current) {
            return;
        }
        // position
        let offset;
        let position;
        if (direction === 'rtl') {
            offset = (_a = tabBarRef.current.getBoundingClientRect().right) !== null && _a !== void 0 ? _a : 0;
            position = activeTab.getBoundingClientRect().right;
        }
        else {
            offset = (_b = tabBarRef.current.getBoundingClientRect().left) !== null && _b !== void 0 ? _b : 0;
            position = activeTab.getBoundingClientRect().left;
        }
        const target = position - offset;
        tabBarRef.current.style.setProperty('--tabs-indicator-transform', `translateX(${target}px)`);
        // width
        const width = activeTab.getBoundingClientRect().width;
        tabBarRef.current.style.setProperty('--tabs-indicator-width', `${width}px`);
    }, [tabsRef, tabBarRef, direction]);
    const updateIndicatorColor = React.useCallback((disabled = false, active = false) => {
        var _a, _b, _c;
        if (disabled) {
            (_a = tabBarRef.current) === null || _a === void 0 ? void 0 : _a.style.setProperty('--tabs-indicator-background', colors.tab.disabled || 'initial');
        }
        else if (active) {
            (_b = tabBarRef.current) === null || _b === void 0 ? void 0 : _b.style.setProperty('--tabs-indicator-background', colors.tab.hover || 'initial');
        }
        else {
            (_c = tabBarRef.current) === null || _c === void 0 ? void 0 : _c.style.setProperty('--tabs-indicator-background', colors.tab.default || 'initial');
        }
    }, [colors.tab.default, colors.tab.disabled, colors.tab.hover, tabBarRef]);
    // update indicator width and position when tabs component becomes visible when it is initially hidden
    React.useEffect(() => {
        const wrapper = tabBarRef.current;
        const tabs = tabsRef.current;
        if (!wrapper || !tabs) {
            return;
        }
        const activeTabObserver = new ResizeObserver((entries, _observer) => {
            const activeTab = entries[0];
            if (activeTab.contentRect.width > 0) {
                updateIndicatorPosition(activeIndex);
            }
        });
        activeTabObserver.observe(tabs[activeIndex]);
        return () => {
            if (tabs) {
                activeTabObserver.unobserve(tabs[activeIndex]);
            }
            else {
                activeTabObserver.disconnect();
            }
        };
    }, [tabBarRef, activeIndex, updateIndicatorPosition, tabsRef]);
    // add resize handler
    React.useEffect(() => {
        const handleResize = () => {
            updateIndicatorPosition(activeIndex);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [activeIndex, updateIndicatorPosition]);
    return {
        updateIndicatorPosition,
        updateIndicatorColor,
    };
};
